import React from 'react'
import { find, forEach } from "lodash";
import {
  Button,
  ControlLabel,
  FormControl,
  FormGroup,
  Modal,
} from "react-bootstrap";
import { GFUser, GFTeacherUser } from './../../models/models';
import constants from "../../constants";
import { toastr } from 'react-redux-toastr';

const FontAwesome = require("react-fontawesome");

interface Props extends React.Props<AddTeacherModal> {
  showAddTeacher: boolean;
  hideAddTeacher: () => void;
  loading: boolean;
  addTeacher: (
    teacher: GFTeacherUser,
  ) => Promise<any>;
  setTeacherToAdd: (teacher: any) => void;
  teacher: GFTeacherUser;
  searchTeachers: (query: string) => GFTeacherUser[],
  refreshTeacherList: () => void,
  user: GFUser,
}

interface State {
  email: string;
  first: string;
  last: string;
  formValidations: any;
  formValid: boolean;
}

class AddTeacherModal extends React.Component<Props, State> {

  /**
   * The constructor
   * @param props
   */
  constructor(props: Props) {
    super(props);
    this.state = {
      email: '',
      first: '',
      last: '',
      formValidations: {
        email: {
          validationState: null,
          validationMessage: "",
          valid: false,
          validators: [
            { required: true, message: "Email is required" },
            {
              pattern: constants.emailRegex,
              message: constants.emailValidationMessage,
            },
          ],
        },
        first: {
          validationState: null,
          validationMessage: "",
          valid: false,
          validators: [{ required: true, message: "First Name is required" }],
        },
        last: {
          validationState: null,
          validationMessage: "",
          valid: false,
          validators: [{ required: true, message: "Last Name is required" }],
        },
      },
      formValid: false,
    }
  }

  /**
   * Get called when component will unmount
   */
  componentWillUnmount() {
    const { setTeacherToAdd } = this.props;
    setTeacherToAdd({});
    this.setState({
      email: '',
      formValid: false,
      first: '',
      last: '',
    });
  }

  /**
   * Validate a Form
   */
  validateForm() {
    const formValid = !find(this.state.formValidations, ["valid", false]);
    this.setState({ formValid });
  }

  /**
   * Validate a Form input
   * @param value
   * @param validator
   * @returns boolean
   */
  validate(value: string, validator: any) {
    let valid = true;
    if (validator.pattern && value.length > 0) {
      const newValue = value.match(validator.pattern);
      valid = newValue ? true : false;
    }
    if (validator.required) {
      valid = !!value.length || false;
    }
    if (validator.mustMatch) {
      valid =
        this.state[validator.mustMatch as keyof State] === value ? true : false;
    }
    return valid;
  }

  /**
   * Perform Form validations
   * @param name
   * @param value
   * @param showErrors
   */
  validateField(name: string, value: string, showErrors: boolean) {
    // loop over each field we need to validate
    forEach(this.state.formValidations, (field, key) => {
      if (key === name) {
        const newFormValidations = Object.assign(
          {},
          this.state.formValidations
        );
        let errorMessage = "";

        // loop over each validation for this field, and set error to the error message if something is invalid
        field.validators.forEach((validator: any) => {
          if (!this.validate(value, validator)) {
            errorMessage = validator.message;
          }
        });
        // after we have checked all the validators, is there an error message for this field?
        if (!errorMessage) {
          // no error message
          newFormValidations[name].validationState = "success";
          newFormValidations[name].validationMessage = "";
          newFormValidations[name].valid = true;
          if (!showErrors) {
            newFormValidations[name].validationState = null;
            newFormValidations[name].validationMessage = "";
          }
        } else {
          // error
          // only show the errors if showErrors
          if (showErrors) {
            newFormValidations[name].validationState = "error";
            newFormValidations[name].validationMessage = errorMessage;
          }

          newFormValidations[name].valid = false;
        }
        this.setState(
          { formValidations: newFormValidations },
          this.validateForm
        );
      }
    });
  }

  /**
   * Gets called when a Form input changes
   * @param e
   */
  handleChange = (e: any) => {
    const value = e.target.value;
    const name = e.target.name;
    this.setState({ [name]: value } as unknown as State, () => {
      this.validateField(name, value, true)
    });
  }

  /**
   * Send selected teacher to API
   */
  addTeacher = () => {
    const { hideAddTeacher, setTeacherToAdd, teacher } = this.props;
    // Send teacher and plan to API
    const teacherPacket = {
      ...teacher,
      first: this.state.first,
      email: this.state.email,
      last: this.state.last,
    };
    this.props.addTeacher(teacherPacket)
    .then((resp:any) => {
      // Close modal.
      this.props.refreshTeacherList();
      toastr.success(
        "Success",
        !!teacherPacket.id ? "Teacher added." : "Your new teacher has been created. We have emailed them their temporary password.",
        constants.toastrSuccess
      );
      this.setState({email: '', first: '', last: '', formValid: false});
      setTeacherToAdd({});
      this.validateForm();
      hideAddTeacher();
    })
    .catch(e => {
      let errorMsg = "Unable to add teacher. Please check the form and try again.";

      if (e.response && e.response.status === 409) {
        errorMsg = "Unable to add teacher. Teacher already exists and has an active subscription or is a member of another group."
      }
      if (e.response && e.response.status === 406) {
        errorMsg = "Unable to add teacher. There is not enough room in current plan to add another teacher with students."
      }
      // Close modal.
      toastr.error(
        "Error",
        errorMsg,
        constants.toastrError
      );
      this.setState({email: '', first: '', last: '', formValid: false});
      setTeacherToAdd({});
      this.validateForm();
      hideAddTeacher();
    });
  }

  /**
   * Renders the component
   *
   * @returns Modal
   */
  render () {
    const { showAddTeacher, hideAddTeacher, loading } = this.props;
    const { email, first, last, formValid } = this.state;
    return (
      <Modal
        show={showAddTeacher}
        onHide={hideAddTeacher}
        container={this}
      >
        <Modal.Header>
          <Modal.Title>
            <span className="body-text">Add Teacher</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form id="teacherForm" onSubmit={() => {}}>
            <FormGroup
              controlId="teacherEmail"
              bsSize="lg"
              validationState={
                this.state.formValidations.email.validationState
              }
            >
              <ControlLabel>Email</ControlLabel>
              <FormControl
                type="text"
                name="email"
                value={email}
                placeholder="First name"
                onChange={this.handleChange}
              />
              <FormControl.Feedback />
            </FormGroup>
            <FormGroup
              controlId="teacherFirstName"
              bsSize="lg"
              validationState={
                this.state.formValidations.first.validationState
              }
            >
              <ControlLabel>First Name</ControlLabel>
              <FormControl
                type="text"
                name="first"
                value={first}
                placeholder="First name"
                onChange={this.handleChange}
              />
              <FormControl.Feedback />
            </FormGroup>
            <FormGroup
              controlId="teacherLastName"
              bsSize="lg"
              validationState={
                this.state.formValidations.last.validationState
              }
            >
              <ControlLabel>Last Name</ControlLabel>
              <FormControl
                type="text"
                name="last"
                value={last}
                placeholder="Last name"
                onChange={this.handleChange}
              />
              <FormControl.Feedback />
            </FormGroup>
            <Button
              disabled={loading || !formValid}
              bsStyle="primary"
              type="button"
              block
              onClick={this.addTeacher}
            >
              <FontAwesome name="plus" />
              <span className="backLink">Add Teacher</span>
            </Button>
          </form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={hideAddTeacher}
            bsStyle="default"
            type="button"
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

export default AddTeacherModal;
